import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltModal } from '@cae/cobalt-react/components/modal';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconHelpCircleOutline } from '@cae/cobalt-react/icons/outline/CobaltIconHelpCircleOutline';
import { CobaltPopover } from '@cae/cobalt-react/utils';
import { useHasPermission } from '@contexts/permissions-provider';
import { ChangeRequest } from '@features/change-request/ChangeRequest';
import * as React from 'react';

import { HelpPanel } from '../help-panel/HelpPanel';
import styles from './HelpButton.module.css';
import { HELP_CHANGE_REQUEST_CONFIG } from './HelpButton.utils';

const HelpButton = React.memo((): React.ReactElement | null => {
  const hasAeContactPermission = useHasPermission('HelpAEContact_View_Org');
  const hasBasicPermission = useHasPermission('HelpBasic_View_Any');
  const hasInternalPermission = useHasPermission('HelpInternal_View_Any');

  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const hasAnyHelpPermission =
    hasAeContactPermission || hasBasicPermission || hasInternalPermission;

  const [isChangeRequestOpen, setIsChangeRequestOpen] = React.useState(false);

  const [anchorElement, setAnchorElement] = React.useState<HTMLElement | null>(
    null
  );

  const handleButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (anchorElement) {
        setAnchorElement(null);
      } else {
        setAnchorElement(event.currentTarget);
      }
    },
    [anchorElement]
  );

  const handleClosePopover = React.useCallback((): void => {
    setAnchorElement(null);
  }, []);

  const handleSubmitRequest = React.useCallback((): void => {
    handleClosePopover();
    setIsChangeRequestOpen(true);
  }, [handleClosePopover]);

  const handleCloseChangeRequest = React.useCallback((): void => {
    setIsChangeRequestOpen(false);
  }, []);

  if (!hasAnyHelpPermission) {
    return null;
  }

  return (
    <>
      <CobaltButton
        variant="ghost"
        onClick={handleButtonClick}
        aria-label="Help"
        data-testid="help-button"
      >
        <CobaltIconHelpCircleOutline size="medium" />
      </CobaltButton>

      {isMobile ? (
        <CobaltModal
          isOpen={Boolean(anchorElement)}
          onClose={handleClosePopover}
          title="Help"
          showCloseButton
        >
          <HelpPanel onSubmitRequest={handleSubmitRequest} />
        </CobaltModal>
      ) : (
        <CobaltPopover
          isOpen={Boolean(anchorElement)}
          anchorEl={anchorElement}
          onClose={handleClosePopover}
          position="bottom-end"
          className={`${styles.popover} elevation-02-lifted`}
        >
          <HelpPanel onSubmitRequest={handleSubmitRequest} />
        </CobaltPopover>
      )}

      {isChangeRequestOpen && (
        <ChangeRequest
          isOpen={isChangeRequestOpen}
          onClose={handleCloseChangeRequest}
          config={HELP_CHANGE_REQUEST_CONFIG}
        />
      )}
    </>
  );
});

HelpButton.displayName = 'HelpButton';

export { HelpButton };
